import { useState } from "preact/hooks"
import { Loader2 } from "lucide-react"
import { api_fetch } from "../../../api/client"
import { Button } from "../../../components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { Textarea } from "../../../components/ui/textarea"
import { toast } from "../../../components/ui/use-toast"
import { accrual_bucket, bank_account, MONTH_SHORT, month_iso } from "./accrual_bank_model"

export interface sweep_target {
  bucket_id: string
  account_id: string
  year: number
  month_index: number
}

/**
 * Record a sweep: accrual the brand decides is no longer needed (entered as a positive amount to
 * release, stored negative) or a top-up. It sits at the same customer × GL × month grain as the
 * plan so the bank shows it and the export carries it.
 */
export function AccrualSweepModal({
  target,
  buckets,
  accounts,
  onClose,
  onSaved,
}: {
  target: sweep_target
  buckets: accrual_bucket[]
  accounts: bank_account[]
  onClose: () => void
  onSaved: () => void
}) {
  const [month_index, setMonthIndex] = useState(target.month_index)
  const [direction, setDirection] = useState<"release" | "add">("release")
  const [amount, setAmount] = useState("")
  const [note, setNote] = useState("")
  const [saving, setSaving] = useState(false)
  const bucket = buckets.find((b) => b.id === target.bucket_id)
  const account = accounts.find((a) => a.id === target.account_id)

  async function save() {
    const cents = Math.round(Number(amount) * 100)
    if (!Number.isFinite(cents) || cents === 0) {
      toast({ title: "Enter an amount", variant: "destructive" })
      return
    }
    setSaving(true)
    const signed = (direction === "release" ? -Math.abs(cents) : Math.abs(cents)) / 100
    const res = await api_fetch("/accrual-bank/entries", {
      method: "POST",
      body: {
        entries: [{ bucket_id: target.bucket_id, account_id: target.account_id, month: month_iso(target.year, month_index), kind: "sweep", amount: signed, note }],
      },
    })
    setSaving(false)
    if (!res.ok) {
      toast({ title: "Could not record the sweep", description: res.error.message, variant: "destructive" })
      return
    }
    onSaved()
  }

  return (
    <Dialog open onOpenChange={(is_open: boolean) => !is_open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Sweep accrual</DialogTitle>
          <DialogDescription>
            {bucket ? `${bucket.rollup} / ${bucket.name}` : "Bucket"} · {account?.label ?? "GL account"}
          </DialogDescription>
        </DialogHeader>
        <div class="space-y-3">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <Label>Month</Label>
              <Select value={String(month_index)} onValueChange={(v: string) => setMonthIndex(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_SHORT.map((m, i) => (
                    <SelectItem key={m} value={String(i)}>
                      {m} {target.year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Direction</Label>
              <Select value={direction} onValueChange={(v: string) => setDirection(v as "release" | "add")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="release">Release (reduce accrual)</SelectItem>
                  <SelectItem value="add">Add to accrual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Amount</Label>
            <Input type="number" step="0.01" min="0" value={amount} onInput={(e: Event) => setAmount((e.target as HTMLInputElement).value)} placeholder="20000.00" />
          </div>
          <div>
            <Label>Note</Label>
            <Textarea value={note} onInput={(e: Event) => setNote((e.target as HTMLTextAreaElement).value)} placeholder="Why this accrual is released, for the close file" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Record sweep
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
